import classNames from 'classnames';
import { type UseSelectSelectedItemChange, useSelect } from 'downshift';

import Icon from 'components/Icon/Icon';
import { useLanguage } from 'store';
import { getMediaUrl } from 'utils/assets';
import useLayout, { LayoutType } from 'utils/layout';

import styles from './Settings.module.scss';

const getFlagUrl = (regionId: number) =>
  getMediaUrl(`svg-icons/flags/${regionId}.svg`);

const DefaultSwitcher = () => {
  const [
    { available: languages, current: currentLanguage },
    { changeLanguage },
  ] = useLanguage();

  return (
    <div className={styles['default-select']}>
      <div className={styles.icon}>
        <Icon name="arrow-down" />
      </div>
      <img
        className={styles.flag}
        src={getFlagUrl(currentLanguage.regionId)}
        alt={currentLanguage.name}
      />
      <select
        value={currentLanguage.code}
        onChange={(e) => changeLanguage(e.currentTarget.value)}
      >
        {languages.map(({ code }) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </select>
    </div>
  );
};

const CustomSwitcher = () => {
  const [
    { available: languages, current: currentLanguage },
    { changeLanguage },
  ] = useLanguage();

  const onChangeHandler = ({
    selectedItem,
  }: UseSelectSelectedItemChange<(typeof languages)[number]>) => {
    if (!selectedItem) return;
    changeLanguage(selectedItem.code);
  };

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
  } = useSelect({
    items: languages,
    onSelectedItemChange: onChangeHandler,
    defaultSelectedItem: currentLanguage,
    selectedItem: currentLanguage,
  });

  return (
    <div className={styles['custom-select']}>
      <button className={styles.button} {...getToggleButtonProps()}>
        <div className={classNames(styles.arrow)}>
          <Icon
            className={classNames({
              [styles.open]: isOpen,
            })}
            name="arrow-down"
          />
        </div>
        <div className={styles['label-container']}>
          <img
            className={styles.flag}
            src={getMediaUrl(`svg-icons/flags/${selectedItem?.regionId}.svg`)}
            alt={selectedItem?.name}
          />
          <span className={styles.label}>{selectedItem?.code}</span>
        </div>
      </button>

      <div
        className={classNames(styles.menu, { [styles['is-closed']]: !isOpen })}
        {...getMenuProps()}
      >
        {isOpen &&
          languages.map((item, index) => (
            <div
              key={item.code}
              className={styles.item}
              {...getItemProps({ item, index })}
            >
              <img
                className={styles.flag}
                src={getFlagUrl(item.regionId)}
                alt={item.name}
              />
              <span className={styles['item-name']}>{item.code}</span>
            </div>
          ))}
      </div>
    </div>
  );
};

const LanguagesSwitcher = () => {
  const layout = useLayout();

  return layout === LayoutType.mobile ? (
    <DefaultSwitcher />
  ) : (
    <CustomSwitcher />
  );
};

export default LanguagesSwitcher;
